import { useEffect, useState } from "react";
import {
  CHECKOUT_STEP_OPTIONS,
  CheckoutStepOptions,
  GetCheckoutResponseData,
  PatchCheckoutRequest,
  PatchCheckoutResponseData,
} from "../../../../types";
import { Input } from "../forms/components/Input";
import { Button, BUTTON_COLORS, BUTTON_TYPE } from "../Button";
import { UNDERLINE_CLASS } from "../../utils";
import { AddressForm } from "../forms";
import {
  generateAddress,
  generateAddressElement,
} from "../../../../shared-utils/location";
import { Loader } from "../Loader";
import { PaymentStep } from "./PaymentStep";
import { toPriceString } from "../Price";

const STEP_DATA = {
  [CHECKOUT_STEP_OPTIONS.EMAIL]: {
    title: "Email",
  },
  [CHECKOUT_STEP_OPTIONS.LOCATION]: {
    title: "Delivery address",
  },
  [CHECKOUT_STEP_OPTIONS.PAYMENT]: { title: "Payment" },
  [CHECKOUT_STEP_OPTIONS.STATUS]: { title: "Status" },
} as const;

export type SubmitCheckoutStepData = { type: CheckoutStepOptions } & (
  | {
      type: typeof CHECKOUT_STEP_OPTIONS.LOCATION;
      data: NonNullable<PatchCheckoutRequest["deliveryLocation"]>;
    }
  | { type: typeof CHECKOUT_STEP_OPTIONS.EMAIL; data: { email: string } }
  | { type: typeof CHECKOUT_STEP_OPTIONS.PAYMENT; data?: any }
);
export type CheckoutStepsProps = {
  stepIdx: number;
  setStepIdx?: (idx: number) => void;
  steps: CheckoutStepOptions[];
  deliveryLocation?:
    | GetCheckoutResponseData["checkout"]["deliveryLocation"]
    | PatchCheckoutResponseData["checkout"]["deliveryLocation"];
  locationError?: {
    message: string;
  };
  contactEmail?:
    | GetCheckoutResponseData["checkout"]["contactEmail"]
    | PatchCheckoutResponseData["checkout"]["contactEmail"];
  payment?:
    | GetCheckoutResponseData["checkout"]["payment"]
    | PatchCheckoutResponseData["checkout"]["payment"];
  // Used to be
  // payment?: {
  //   subtotal: number;
  //   tax: number;
  //   taxBreakdown: Stripe.Tax.Calculation.TaxBreakdown[];
  //   total: number;
  //   currency: CurrencyType;
  //   clientSecret?: string;
  // };
  status?: { status: keyof typeof STATUS_CONTENT_MAP };
  onSubmit: (data: SubmitCheckoutStepData) => void | Promise<void>;
  showAll?: boolean;
  // cartSlug?: string;
  orderSlug?: string;
};

export const CheckoutSteps = ({ setStepIdx, ...props }: CheckoutStepsProps) => {
  const Step = ({
    type,
    active,
  }: {
    type: CheckoutStepOptions;
    active?: boolean;
  }) => {
    const [email, setEmail] = useState(props.contactEmail);
    useEffect(() => {
      setEmail(props.contactEmail);
    }, [setEmail, props.contactEmail]);

    const statusContent = props.status
      ? STATUS_CONTENT_MAP[props.status?.status]
      : STATUS_CONTENT_MAP["default"];

    const content =
      type === CHECKOUT_STEP_OPTIONS.EMAIL
        ? {
            active: (
              <form
                className="px-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (email) {
                    props.onSubmit({ type: "email", data: { email } });
                  }
                }}
              >
                <Input
                  label="Email"
                  value={email}
                  onChange={(e) => {
                    const { value } = e.target;
                    setEmail(value);
                  }}
                />
                <div className="text-xs">
                  We will send order updates to this email and share it with our
                  artist in case there is any follow up info needed.
                </div>
                <Button type="submit" btnType="small" btnColor="secondary">
                  Confirm
                </Button>
              </form>
            ),
            drawer: props.contactEmail
              ? props.contactEmail + " - Will be shared with artist."
              : "No email set",
            notActive: (
              <div className="px-1">
                <div>
                  <p className="text-xl">{props.contactEmail}</p>
                  <p className="text-sm">
                    We will share this email with our artist in case there is
                    any follow up info needed.
                  </p>
                  <p className="text-sm">
                    If you would like to use a different email, please log in
                    with that email.
                  </p>
                  {/* {setStepIdx ? (
                  <Button
                    btnColor="secondary"
                    btnType="small"
                    onClick={() => {
                      const idx = props.steps.findIndex(
                        (step) => step === CHECKOUT_STEP_OPTIONS.EMAIL,
                      );
                      setStepIdx(idx);
                    }}
                  >
                    Edit
                  </Button>
                ) : null} */}
                </div>
              </div>
            ),
          }
        : type === CHECKOUT_STEP_OPTIONS.LOCATION
          ? {
              active: (
                <div className="px-1">
                  {props.locationError ? (
                    <div className="text-textWarning">
                      <h1>
                        {props.locationError?.message.split(" ").map((word) =>
                          word.includes("@") && word.includes(".") ? (
                            <>
                              <a href={word} className={UNDERLINE_CLASS}>
                                {word}
                              </a>{" "}
                            </>
                          ) : (
                            word + " "
                          ),
                        )}
                      </h1>
                    </div>
                  ) : null}
                  <AddressForm
                    onSubmit={async (data) => {
                      await props.onSubmit({
                        type: CHECKOUT_STEP_OPTIONS.LOCATION,
                        data,
                      });
                    }}
                    values={props.deliveryLocation}
                  />
                </div>
              ),
              drawer: props.deliveryLocation
                ? generateAddress(props.deliveryLocation, {
                    format: "nameAddressZip",
                  })
                : "No address set",
              notActive: (
                <div className="px-1">
                  <div className="">
                    {props.deliveryLocation
                      ? generateAddressElement(props.deliveryLocation)
                      : "No address set"}
                  </div>
                  {setStepIdx ? (
                    <Button
                      className=""
                      btnColor="secondary"
                      btnType="small"
                      onClick={() => {
                        const idx = props.steps.findIndex(
                          (step) => step === CHECKOUT_STEP_OPTIONS.LOCATION,
                        );
                        setStepIdx(idx);
                      }}
                    >
                      Edit
                    </Button>
                  ) : null}
                </div>
              ),
            }
          : type === CHECKOUT_STEP_OPTIONS.PAYMENT
            ? {
                active: props.payment?.clientSecret ? (
                  <PaymentStep
                    onSubmit={() =>
                      props.onSubmit({ type: CHECKOUT_STEP_OPTIONS.PAYMENT })
                    }
                    taxBreakdown={
                      props.payment.taxCalculation?.taxBreakdown ?? []
                    }
                    clientSecret={props.payment.clientSecret}
                    tax={props.payment.taxCalculation?.taxAmount ?? 0}
                    subtotal={props.payment.subtotal}
                    total={props.payment.total}
                    currency={props.payment.currency}
                  />
                ) : (
                  <Loader>Loading stripe data</Loader>
                ),
                drawer: props.payment
                  ? toPriceString({
                      item: {
                        price: props.payment.subtotal,
                        currency: props.payment.currency,
                      },
                    }) +
                    (props.payment.taxCalculation?.taxAmount === 0
                      ? ""
                      : " + " +
                        (props.payment.taxCalculation?.taxAmount
                          ? toPriceString({
                              item: {
                                price: props.payment.taxCalculation?.taxAmount,
                                currency: props.payment.currency,
                              },
                            })
                          : "tax"))
                  : "",
                notActive:
                  "Please confirm your email and submit your address to pay",
              }
            : type === CHECKOUT_STEP_OPTIONS.STATUS
              ? {
                  active: (
                    <div className="px-1 ">
                      <div className="flex" id="payment-status">
                        <div
                          id="status-icon"
                          className=" flex-none text-center p-auto flex items-center mx-2"
                          // style={{ color: content.iconColor }}
                        >
                          <statusContent.icon fill={statusContent.iconColor} />
                        </div>
                        <h2 id="status-text">
                          {statusContent.text}
                          {props.orderSlug
                            ? " - Order #" + props.orderSlug
                            : // props.cartSlug
                              // ? " - Cart #" + props.cartSlug
                              ""}
                        </h2>
                      </div>

                      <div className="p-3">
                        <div>
                          <span>
                            Reach out
                            {props.orderSlug
                              ? ` if you have any questions.`
                              : ` with your contact email if you don't get an email confirming your order within 5 mins!`}
                          </span>
                        </div>
                        <div>
                          <a
                            className={BUTTON_COLORS.text + " !my-0"}
                            href="mailto:sales@doridium.com"
                          >
                            sales@doridium.com
                          </a>
                        </div>
                      </div>
                    </div>
                  ),
                  drawer: props.status?.status,
                  notActive: (
                    <div className="px-1 ">
                      <div className="flex" id="payment-status">
                        <div
                          id="status-icon"
                          className=" flex-none text-center p-auto flex items-center mx-2"
                          // style={{ color: content.iconColor }}
                        >
                          <statusContent.icon fill={statusContent.iconColor} />
                        </div>
                        <h2 id="status-text">{statusContent.text}</h2>
                      </div>
                      {props.orderSlug ? (
                        <span>Order #{props.orderSlug}</span>
                      ) : (
                        <div>
                          <span>
                            Unknown order, please reach out immediately
                          </span>
                        </div>
                      )}

                      <div>
                        <span>
                          {
                            "Reach out with the cart number if you don't get an email confirming your order within 5 mins."
                          }
                        </span>
                      </div>
                      <a
                        className={BUTTON_TYPE.text + BUTTON_COLORS.text}
                        href="mailto:sales@doridium.com"
                      >
                        sales@doridium.com
                      </a>
                    </div>
                  ),
                }
              : null;
    const [show, setShow] = useState(active || props.showAll);
    return (
      <div className="">
        <Button
          btnType="none"
          btnColor="none"
          className={
            " z-20 bg-secondary border-solid  w-full rounded text-left p-2 px-3 " +
            (!props.showAll
              ? " hover:shadow-xl  cursor-pointer "
              : " cursor-default ")
          }
          onClick={() => {
            setShow((show) => props.showAll || !show);
          }}
        >
          <h2 className="text-h2">
            {(active ? "> " : "") +
              STEP_DATA[type]?.title +
              (content?.drawer && !props.showAll ? ": " : "")}
            {props.showAll ? null : content?.drawer}
          </h2>
        </Button>
        <div
          className={
            " -mt-5 mb-4 pt-7 px-5 pb-2 transition-all overflow-auto duration-700 border-solid border-secondary border-l-2 border-b-2  border-r-2 rounded-xl " +
            (show
              ? " max-h-[1000px] h-auto opacity-1  shadow-lg"
              : " pointer-events-none max-h-0 opacity-0")
          }
        >
          {active ? content?.active : content?.notActive}
        </div>
      </div>
    );
  };

  return (
    <div className="">
      {props.steps
        .map((step, idx) => {
          const stepComponent = (
            <Step key={idx} type={step} active={idx === props.stepIdx} />
          );
          return stepComponent ? { step, stepComponent } : stepComponent;
        })
        .filter((item) => !!item)
        .map((item) => item.stepComponent)}
    </div>
  );
};

const SuccessIcon = ({ fill }: { fill: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 14"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.4695 0.232963C15.8241 0.561287 15.8454 1.1149 15.5171 1.46949L6.14206 11.5945C5.97228 11.7778 5.73221 11.8799 5.48237 11.8748C5.23253 11.8698 4.99677 11.7582 4.83452 11.5681L0.459523 6.44311C0.145767 6.07557 0.18937 5.52327 0.556912 5.20951C0.924454 4.89575 1.47676 4.93936 1.79051 5.3069L5.52658 9.68343L14.233 0.280522C14.5613 -0.0740672 15.1149 -0.0953599 15.4695 0.232963Z"
      fill={fill}
    />
  </svg>
);

const ErrorIcon = ({ fill }: { fill: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.25628 1.25628C1.59799 0.914573 2.15201 0.914573 2.49372 1.25628L8 6.76256L13.5063 1.25628C13.848 0.914573 14.402 0.914573 14.7437 1.25628C15.0854 1.59799 15.0854 2.15201 14.7437 2.49372L9.23744 8L14.7437 13.5063C15.0854 13.848 15.0854 14.402 14.7437 14.7437C14.402 15.0854 13.848 15.0854 13.5063 14.7437L8 9.23744L2.49372 14.7437C2.15201 15.0854 1.59799 15.0854 1.25628 14.7437C0.914573 14.402 0.914573 13.848 1.25628 13.5063L6.76256 8L1.25628 2.49372C0.914573 2.15201 0.914573 1.59799 1.25628 1.25628Z"
      fill={fill}
    />
  </svg>
);

const InfoIcon = ({ fill }: { fill: string }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 1.5H4C2.61929 1.5 1.5 2.61929 1.5 4V10C1.5 11.3807 2.61929 12.5 4 12.5H10C11.3807 12.5 12.5 11.3807 12.5 10V4C12.5 2.61929 11.3807 1.5 10 1.5ZM4 0C1.79086 0 0 1.79086 0 4V10C0 12.2091 1.79086 14 4 14H10C12.2091 14 14 12.2091 14 10V4C14 1.79086 12.2091 0 10 0H4Z"
      fill={fill}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.25 7C5.25 6.58579 5.58579 6.25 6 6.25H7.25C7.66421 6.25 8 6.58579 8 7V10.5C8 10.9142 7.66421 11.25 7.25 11.25C6.83579 11.25 6.5 10.9142 6.5 10.5V7.75H6C5.58579 7.75 5.25 7.41421 5.25 7Z"
      fill="white"
    />
    <path
      d="M5.75 4C5.75 3.31075 6.31075 2.75 7 2.75C7.68925 2.75 8.25 3.31075 8.25 4C8.25 4.68925 7.68925 5.25 7 5.25C6.31075 5.25 5.75 4.68925 5.75 4Z"
      fill="white"
    />
  </svg>
);

const STATUS_CONTENT_MAP = {
  // Doridium checkout statuses
  finished: {
    text: "Checkout finished",
    iconColor: "#30B130",
    icon: SuccessIcon,
  },
  submitted: {
    text: "Your checkout has been submitted",
    iconColor: "#6D6E78",
    icon: InfoIcon,
  },
  ready: {
    text: "Your checkout is ready to be completed",
    iconColor: "#6D6E78",
    icon: InfoIcon,
  },
  open: {
    text: "Your checkout is still open",
    iconColor: "#6D6E78",
    icon: InfoIcon,
  },
  // Stripe statuses
  succeeded: {
    text: "Payment succeeded",
    iconColor: "#30B130",
    icon: SuccessIcon,
  },
  processing: {
    text: "Your payment is processing.",
    iconColor: "#6D6E78",
    icon: InfoIcon,
  },
  loading: {
    text: "Page is loading",
    iconColor: "#6D6E78",
    icon: InfoIcon,
  },
  requires_payment_method: {
    text: "Your payment was not successful, please try again.",
    iconColor: "#DF1B41",
    icon: ErrorIcon,
  },
  default: {
    text: "Something went wrong, please try again.",
    iconColor: "#DF1B41",
    icon: ErrorIcon,
  },
};
